import { useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '../Alert';
import Button from '@mui/material/Button';
import { Table, Td, Th, Tr } from '../Table';
import useSortableData from '../../modules/useSortableData';
import Checkbox from '@mui/material/Checkbox';
import { getDataByUrl } from '../../modules/fetch';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDeleteStoreMutation } from '../../api/storeAPI';
import { fromNowSeconds, toFromNow } from '../../modules/renderDate';
import { LAZADA_SUBSCRIPTION_STATUS_EXPIRED } from '../../constants/LazadaSubscription';
import ListItemText from '@mui/material/ListItemText';
import moment from 'moment/moment';
import { AlertTitle, ButtonsContainer, Container, Logo, Text } from './style';
import { Link, useNavigate } from 'react-router-dom';
import getWhatsappQR from '../../api/getWhatsappQR';
import Popup from '../Popup';
import QRCode from 'react-qr-code';

export default function DataGridDemo({ data, handleRegistrationPopup = null }) {
  const navigate = useNavigate();
  const intl = useIntl();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const currentItemId = useRef(null);
  const { items, requestSort, sortConfig } = useSortableData(data);
  const [deleteStore] = useDeleteStoreMutation();
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [qrValue, setQrValue] = useState<string>('');

  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const handleStoreView = (storeId) => () => {
    navigate(`/store/${storeId}`);
  };

  const handleDelete = async () => {
    if (currentItemId.current) {
      await deleteStore(currentItemId.current);
      setShowDeletePopup(false);
    }
  };

  const handleBtnDelete = (storeId: string) => {
    currentItemId.current = storeId;
    setShowDeletePopup(true);
  };

  const handleCheck = (id) => {
    console.debug('/components/StoresList/index.js:handleCheck', id);
  };

  const handleStoreConnect = async (item: any) => {
    if (item.nextConnectionURL.includes('whatsapp_qr_app')) {
      try {
        const qr = await getWhatsappQR(item?._id?.$oid);
        setQrValue(qr);
        setShowQRPopup(true);
      } catch (e) {
        console.error(e);
      }
      return;
    } else {
      handleRedirect(item.nextConnectionURL);
    }
  };

  const handleRedirect = (storeAuthUrl) => {
    getDataByUrl(storeAuthUrl)
      .then((data) => {
        window.location = data.data.redirect;
      })
      .catch((err) => {
        console.error(err);
      });

    return true;
  };

  const isStoreNeedsToConnect = (item) => {
    const apps = item?.apps || {};

    let isAppExpires = false;
    for (const appKey in apps) {
      if (apps[appKey] && apps[appKey].isExpired === true) {
        isAppExpires = true;
        break;
      }
    }

    return (item.connectionStatus !== 'connected' && item.nextConnectionURL !== '') || isAppExpires;
  };

  const calculateExpirationStatus = (app, subscription) => {
    const now = moment();
    const SEVEN_DAYS_IN_MS = 86400 * 7 * 1000; // milliseconds for subscription
    const SEVEN_DAYS_IN_SEC = 86400 * 7; // seconds for auth
    const hasValidSubscription =
      subscription != null && typeof subscription === 'object' && subscription.end_time != null;

    if (hasValidSubscription) {
      // Subscription-based expiration (end_time is Unix timestamp in milliseconds)
      const subscriptionEndTimeMs = subscription?.end_time ?? 0;
      const isExpired =
        now.valueOf() > subscriptionEndTimeMs ||
        subscription?.status === LAZADA_SUBSCRIPTION_STATUS_EXPIRED;
      const nearlyExpired = now.valueOf() + SEVEN_DAYS_IN_MS > subscriptionEndTimeMs;
      const retrievedAt = app?.auth?.retrievedAt?.date
        ? moment(app.auth.retrievedAt.date).unix()
        : now.unix();
      const expireTime = subscriptionEndTimeMs / 1000 - retrievedAt;

      return { isExpired, nearlyExpired, expireTime };
    } else {
      // Auth-based expiration (Unix timestamp in seconds)
      const authExpireTime = app?.auth?.refreshExpires ?? 0;
      const retrievedAt = app?.auth?.retrievedAt?.date
        ? moment(app.auth.retrievedAt.date).unix()
        : now.unix();
      const totalExpireTime = authExpireTime + retrievedAt;

      const isExpired = now.unix() > totalExpireTime;
      const nearlyExpired = now.unix() + SEVEN_DAYS_IN_SEC > totalExpireTime;

      return { isExpired, nearlyExpired, expireTime: authExpireTime };
    }
  };

  return (
    <>
      {showDeletePopup && (
        <Alert handleClose={() => setShowDeletePopup(false)}>
          <AlertTitle>
            <FormattedMessage
              id="storeManagement.storeList.alert.title"
              defaultMessage="Are you sure you want to delete this store?"
            />
          </AlertTitle>
          <ButtonsContainer>
            <Button variant="outlined" onClick={() => setShowDeletePopup(false)}>
              <FormattedMessage
                id="storeManagement.storeList.alert.button.no"
                defaultMessage="No"
              />
            </Button>
            <Button variant="contained" color="primary" onClick={handleDelete}>
              <FormattedMessage
                id="storeManagement.storeList.alert.button.yes"
                defaultMessage="Yes"
              />
            </Button>
          </ButtonsContainer>
        </Alert>
      )}
      {showQRPopup && (
        <Popup
          handleClose={() => setShowQRPopup(false)}
          isMedia={false}
          isPreviewTicket={false}
          setIsPreviewTicket={false}
        >
          <div>
            <FormattedMessage
              id="storeManagement.qr.popup.message"
              defaultMessage="Scan the QR code with your WhatsApp mobile app to connect your store."
            />
            <div style={{ marginTop: 24 }}>
              {qrValue ? <QRCode value={qrValue} size={220} /> : <p>Loading…</p>}
            </div>
          </div>
        </Popup>
      )}

      <Container>
        <Table>
          <thead>
            <Tr>
              <Th></Th>
              <Th>
                <FormattedMessage
                  id="storeManagement.storeList.table.platform"
                  defaultMessage="Platform"
                />
              </Th>
              <Th>
                <button
                  type="button"
                  onClick={() => requestSort('name')}
                  className={getClassNamesFor('name')}
                >
                  <FormattedMessage
                    id="storeManagement.storeList.table.shop"
                    defaultMessage="Shop Name"
                  />
                </button>
              </Th>
              <Th>
                <button
                  type="button"
                  onClick={() => requestSort('status')}
                  className={getClassNamesFor('status')}
                >
                  <FormattedMessage
                    id="storeManagement.storeList.table.status"
                    defaultMessage="Connection Status"
                  />
                </button>
              </Th>
              <Th>
                <FormattedMessage
                  id="storeManagement.storeList.table.actions.label"
                  defaultMessage="Actions"
                />
              </Th>
            </Tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <Tr
                key={`table-tr-${index}`}
                className={isStoreNeedsToConnect(item) ? 'disconnected-row' : ''}
              >
                <Td>
                  <Checkbox
                    color="primary"
                    value="agree"
                    onChange={() => handleCheck(item?._id?.$oid)}
                  />
                </Td>
                <Td>
                  <Logo>
                    <img src={`/pics/${item.provider ?? 'default'}_small.png`} alt="" />
                  </Logo>
                </Td>
                <Td>
                  <Link to={`/store/${item?._id?.$oid}`}>
                    {item.name}&nbsp;
                    {item.countryCode?.toUpperCase()}
                  </Link>
                </Td>
                <Td>
                  <Text status={item.connectionStatus}>{item.connectionMessage}</Text>
                  {item.connectionStatus !== 'connected' && item.nextConnectionURL !== '' ? (
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={() => handleStoreConnect(item)}
                    >
                      <FormattedMessage
                        id="storeManagement.storeList.table.connect"
                        defaultMessage="Connect"
                      />
                    </Button>
                  ) : (
                    ''
                  )}
                  {item.provider === 'whatsapp' && ''}
                  {item.provider === 'shopee' &&
                    Object.keys(item.apps).map((connector, index) => {
                      const needReauthorize =
                        moment(item.apps[connector].auth?.retrievedAt?.date).unix() <
                        moment().unix() - 86400 * 30;
                      const isExpired =
                        moment().unix() >
                        item.apps[connector].rawResponse?.sellerInfoResponse?.expire_time;

                      // notice 7 days before expiration
                      const nearlyExpired =
                        moment().unix() + 86400 * 7 >
                        item.apps[connector].rawResponse?.sellerInfoResponse?.expire_time;

                      const textColor =
                        isExpired || needReauthorize ? 'red' : nearlyExpired ? 'orange' : 'inherit';

                      if (needReauthorize && (!isExpired || !nearlyExpired)) {
                        return (
                          <ListItemText
                            key={`store_management_store_list_table_expires-connector-${index}`}
                            sx={{
                              padding: 0,
                              margin: 0,
                              color: 'red',
                            }}
                            primary={
                              index +
                              1 +
                              '. ' +
                              intl.formatMessage({
                                id: 'storeManagement.storeList.table.needReauthorize.' + connector,
                                defaultMessage: connector,
                              })
                            }
                          />
                        );
                      }

                      return (
                        <ListItemText
                          key={`store_management_store_list_table_expires-connector-${index}`}
                          sx={{
                            padding: 0,
                            margin: 0,
                            color: textColor,
                          }}
                          primary={
                            index +
                            1 +
                            '. ' +
                            intl.formatMessage(
                              {
                                id: 'storeManagement.storeList.table.expires.' + connector,
                                defaultMessage: connector,
                              },
                              {
                                expire: item.apps[connector].rawResponse?.sellerInfoResponse
                                  ?.expire_time
                                  ? fromNowSeconds(
                                      item.apps[connector].rawResponse?.sellerInfoResponse
                                        ?.expire_time,
                                    )
                                  : 'unknown',
                              },
                            )
                          }
                        />
                      );
                    })}
                  {item.provider === 'tiktok' &&
                    Object.keys(item.apps).map((connector, index) => {
                      const isExpired =
                        moment().unix() > item.apps[connector].auth?.refreshExpires;

                      // notice 7 days before expiration
                      const nearlyExpired =
                        moment().unix() + 86400 * 7 >
                        item.apps[connector].auth?.refreshExpires;

                      const textColor = isExpired ? 'red' : nearlyExpired ? 'orange' : 'inherit';

                      if (isExpired) {
                        return (
                          <ListItemText
                            key={`store_management_store_list_table_expires-connector-${index}`}
                            sx={{
                              padding: 0,
                              margin: 0,
                              color: 'red',
                            }}
                            primary={
                              index +
                              1 +
                              '. ' +
                              intl.formatMessage({
                                id: 'storeManagement.storeList.table.needReauthorize.' + connector,
                                defaultMessage: connector,
                              })
                            }
                          />
                        );
                      }

                      return (
                        <ListItemText
                          key={`store_management_store_list_table_expires-connector-${index}`}
                          sx={{
                            padding: 0,
                            margin: 0,
                            color: textColor,
                          }}
                          primary={
                            index +
                            1 +
                            '. ' +
                            intl.formatMessage(
                              {
                                id: 'storeManagement.storeList.table.expires.' + connector,
                                defaultMessage: connector,
                              },
                              {
                                expire: item.apps[connector].auth?.refreshExpires
                                  ? fromNowSeconds(
                                      item.apps[connector].auth?.refreshExpires,
                                    )
                                  : 'unknown',
                              },
                            )
                          }
                        />
                      );
                    })}
                  {item.provider === 'lazada' &&
                    Object.keys(item.apps).map((connector, index) => {
                      const app = item.apps[connector];
                      const subscription = app?.subscription;

                      const { isExpired, nearlyExpired, expireTime } = calculateExpirationStatus(
                        app,
                        subscription,
                      );
                      const textColor = isExpired ? 'red' : nearlyExpired ? 'orange' : 'inherit';

                      if (expireTime < 0) {
                        return (
                          <ListItemText
                            key={`store_management_store_list_table_expires-connector-${index}`}
                            sx={{
                              padding: 0,
                              margin: 0,
                              color: 'red',
                            }}
                            primary={
                              index +
                              1 +
                              '. ' +
                              intl.formatMessage({
                                id: 'storeManagement.storeList.table.needReauthorize.' + connector,
                                defaultMessage: connector,
                              })
                            }
                          />
                        );
                      }
                      return (
                        <ListItemText
                          key={`store_management_store_list_table_expires-connector-${index}`}
                          sx={{
                            padding: 0,
                            margin: 0,
                            color: textColor,
                          }}
                          primary={
                            index +
                            1 +
                            '. ' +
                            intl.formatMessage(
                              {
                                id: 'storeManagement.storeList.table.expires.' + connector,
                                defaultMessage: connector,
                              },
                              {
                                expire: toFromNow(
                                  expireTime,
                                  app?.auth?.retrievedAt?.date
                                    ? moment(app.auth.retrievedAt.date).unix()
                                    : moment().unix(),
                                ),
                              },
                            )
                          }
                        />
                      );
                    })}
                </Td>
                <Td>
                  {/* <IconButton aria-label="hide" onClick={handleHide}>
                  <VisibilityOffIcon fontSize="small" />
                </IconButton>
                <IconButton aria-label="settings" onClick={handleSettings}>
                  <SettingsIcon fontSize="small" />
                </IconButton> */}
                  {item.provider === 'whatsapp' && (
                    <>
                      <IconButton
                        aria-label="settings"
                        onClick={handleRegistrationPopup(item?._id?.$oid)}
                      >
                        <AppRegistrationIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="export"
                        onClick={() => navigate(`/store/export/${item?._id?.$oid}`)}
                      >
                        <ImportExportIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {item.provider === 'whatsapp_qr' && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        handleBtnDelete(item?._id?.$oid);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton aria-label="settings" onClick={handleStoreView(item?._id?.$oid)}>
                    <ArrowCircleRightOutlinedIcon fontSize="small" />
                  </IconButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Container>
    </>
  );
}
